import {
  HubStorageCapacityServiceId,
  HubStorageCapacityStatus,
} from '@robovision/hub'
import {
  groupBy,
  sortByDate,
} from '@robovision/rvai-utils'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { StorageCapacity } from '~domain/storage/StorageCapacity'
import { STATUS_PRIORITY } from '~domain/storage/StorageCapacityStatus'
import { useStoragesStore } from '../useStoragesStore'


export function useStorages () {
  const store = useStoragesStore()
  const { getStorage } = store
  const {
    storages,
  } = storeToRefs(store)

  const isNormalStorageStatus = computed(() => storageStatus.value === HubStorageCapacityStatus.NORMAL)

  const algorithmStorage = computed(() => getStorage(HubStorageCapacityServiceId.Algorithm))
  const dataStorage = computed(() => getStorage(HubStorageCapacityServiceId.Data))
  const databaseStorage = computed(() => getStorage(HubStorageCapacityServiceId.Database))

  const groupedStorages = computed(() => groupBy([
    dataStorage.value,
    algorithmStorage.value,
    databaseStorage.value,
  ].filter(Boolean), 'deviceId'))

  const singleStorageGroupWithAllStorageTypes = computed(() => {
    const firstGroup = groupedStorages.value.values().next().value
    return groupedStorages.value.size === 1 && firstGroup.length === 3 ? firstGroup : undefined
  })

  const storageColor = computed(() => {
    switch (storageStatus.value) {
      case HubStorageCapacityStatus.ALARM:
        return 'negative'
      case HubStorageCapacityStatus.WARNING:
        return 'warning'
      default:
        return 'positive'
    }
  })

  const storageStatus = computed(() => {
    if (singleStorageGroupWithAllStorageTypes.value) {
      const firstGroup: StorageCapacity[] = groupedStorages.value.values().next().value
      const storage = sortByDate(firstGroup, 'updatedAt').at(-1)!

      return storage.status
    }

    const filteredItems = storages.value
      // We need to keep only defined services ids
      .filter(s => Object.values(HubStorageCapacityServiceId).includes(s.serviceId))
      .filter(s => STATUS_PRIORITY.includes(s.status))

    if (!filteredItems.length) return HubStorageCapacityStatus.NORMAL
    return filteredItems.some(s => s.status === STATUS_PRIORITY[0])
      ? HubStorageCapacityStatus.ALARM
      : HubStorageCapacityStatus.WARNING
  })

  return {
    groupedStorages,
    isNormalStorageStatus,
    singleStorageGroupWithAllStorageTypes,
    storageColor,
    storageStatus,
  }
}
